import React, { useState, useEffect } from "react";
import getOptimizedUrl from '../utils/imageUtils';
import { useNavigate } from 'react-router-dom';
import { getProducts } from '../services/productService';

export default function ProductShowcase() {
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState("roz");
  const [rozRozProducts, setRozRozProducts] = useState([]);
  const [kuchRozProducts, setKuchRozProducts] = useState([]);
  const [rozCurrentIndex, setRozCurrentIndex] = useState(0);
  const [kuchCurrentIndex, setKuchCurrentIndex] = useState(0);

  const notifyHomeContentUpdated = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("home:content-updated"));
    }
  };

  // Fetch products on mount
  useEffect(() => {
    const fetchCollectionProducts = async () => {
      try {
        const allProducts = await getProducts();

        // Filter products by category
        const rozRoz = allProducts.filter(p => p.category === "Roz Roz");
        const kuchRoz = allProducts.filter(p => p.category === "Kuch Roz");

        setRozRozProducts(rozRoz);
        setKuchRozProducts(kuchRoz);
        notifyHomeContentUpdated();
        setTimeout(notifyHomeContentUpdated, 150);
      } catch (error) {
        console.error("Error fetching collection products:", error);
      }
    };

    fetchCollectionProducts();
  }, []);

  const activeProducts = activeTabId === "roz" ? rozRozProducts : kuchRozProducts;
  const currentIndex = activeTabId === "roz" ? rozCurrentIndex : kuchCurrentIndex;
  const setCurrentIndex = activeTabId === "roz" ? setRozCurrentIndex : setKuchCurrentIndex;

  // Main product is always the first one (doesn't change)
  const mainProduct = activeProducts[0];
  // Thumbnail cycles through all products
  const thumbnailProduct = activeProducts[currentIndex];

  const collectionName = activeTabId === "roz" ? "Roz Roz" : "Kuch Roz";

  // Use specific hero covers for the two collections per design request
  const rozCover = 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/roz%20roz%20cover-1770988570206-a042a987a279f5e1.webp';
  const kuchCover = 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/9UUI-1773681573008-78db11cfa6ef59b2.webp';
  const heroImageUrl = activeTabId === 'kuch' ? kuchCover : rozCover;

  // Preload both cover images on mount so tab switches are instant
  useEffect(() => {
    [rozCover, kuchCover].forEach((src) => {
      const img = new Image();
      img.src = getOptimizedUrl(src, 1200);
    });
  }, []);

  // Auto-advance thumbnail carousel every 3 seconds
  useEffect(() => {
    if (activeProducts.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activeProducts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [activeProducts.length, setCurrentIndex]);

  const NavItem = ({ number, name, id }) => {
    const isActive = activeTabId === id;
    return (
      <div className={`relative z-20 w-full pb-2 ${isActive ? 'border-b-2 border-black' : 'border-b-2 border-gray-300'}`}>
        <button
          type="button"
          onClick={() => setActiveTabId(id)}
          onTouchStart={() => setActiveTabId(id)}
          className="w-full text-left flex items-center gap-6 md:gap-16 touch-manipulation pointer-events-auto"
        >
          <div
            className={`text-xs md:text-base tracking-wide font-semibold font-bdogrotesk whitespace-nowrap ${
              isActive ? "text-black" : "text-gray-400"
            }`}
          >
            {number}
          </div>
          <div
            className={`pt-0 md:pt-2 text-2xl md:text-6xl leading-[1.05] font-semibold font-bdogrotesk ${
              isActive ? "text-black" : "text-gray-400"
            }`}
          >
            {name}
          </div>
        </button>
      </div>
    );
  };

  if (!mainProduct) {
    return (
      <section className="mx-auto max-w-screen overflow-hidden py-8 px-4 md:px-0">
        <div className="text-center text-gray-500 py-20">
          Loading collection...
        </div>
      </section>
    );
  }

  return (
    // RESPONSIVE: Added px-4 for mobile padding
    <section className="mx-auto max-w-screen overflow-hidden py-8 px-2 md:px-0">

      {/* FLEX CONTAINER REPLACING GRID */}
      {/* Mobile: flex-col (vertical stack) | Desktop: flex-row (horizontal) */}
      <div className="flex flex-col lg:flex-row gap-y-10 lg:gap-y-0 gap-x-6 lg:gap-x-4">

        {/* Left rail - 4/12 width on desktop (33.3%) */}
        <aside className="relative z-20 w-full lg:w-4/12 pt-4 flex-shrink-0 pointer-events-auto">
          <div className="space-y-2">
            <NavItem
              number="001"
              name="Roz Roz"
              id="roz"
            />
            <NavItem
              number="002"
              name="Kuch Roz"
              id="kuch"
            />
          </div>
        </aside>

        {/* Middle: hero + name + CTA bar - 6/12 width on desktop (50%) */}
        <div className="w-full lg:w-6/12 flex-shrink-0">
          <div className="bg-white relative">
            <img
              src={getOptimizedUrl(heroImageUrl, 1200)}
              alt={mainProduct?.product || mainProduct?.title || 'Collection cover'}
              className="w-full object-cover bg-white aspect-[3/4] md:aspect-auto md:h-[80vh] lg:h-[120vh]"
              draggable={false}
            />
          </div>

          {/* Product name under image (small, grey) */}
          {/* <div className="mt-6 text-[15px] text-gray-700 font-bdogrotesk">
            {mainProduct.product || mainProduct.title}
          </div> */}

          {/* Full-width black CTA bar with price on right */}
          <button
            type="button"
            onClick={() => navigate(`/store?collection=${encodeURIComponent(collectionName)}`)}
            className="home-body-text group mt-2 w-full bg-black text-white px-3 py-2 flex items-center justify-between text-base font-medium tracking-wide font-bdogrotesk"
          >
            {/* === ANIMATED "SHOP NOW" (LEFT SIDE) === */}
            <div className="relative h-5 overflow-hidden">
              <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                <span className="flex h-5 items-center">Shop now</span>
                <span className="flex h-5 items-center">Shop now</span>
              </div>
            </div>

            {/* === ANIMATED PRICE (RIGHT SIDE) === */}
            {/* <div className="relative h-5 overflow-hidden">
              <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                <span className="flex h-5 items-center text-white/90">
                  ₹ {mainProduct.discountedPrice?.toLocaleString('en-IN') || mainProduct.price?.toLocaleString('en-IN') || mainProduct.mrp?.toLocaleString('en-IN')}
                </span>
                <span className="flex h-5 items-center text-white/90">
                  ₹ {mainProduct.discountedPrice?.toLocaleString('en-IN') || mainProduct.price?.toLocaleString('en-IN') || mainProduct.mrp?.toLocaleString('en-IN')}
                </span>
              </div>
            </div> */}
          </button>
        </div>

        {/* Right: thumb + copy - hidden on mobile, 2/12 width on desktop (16.6%) */}
        <aside className="hidden lg:block lg:w-2/12 flex-shrink-0 pr-8">
          <div className="space-y-2">
            <div className="bg-white">
              <img
                src={getOptimizedUrl(thumbnailProduct?.images?.[1] || thumbnailProduct?.images?.[0] || '', 800)}
                alt={`${thumbnailProduct?.product || thumbnailProduct?.title} detail`}
                className="w-full object-cover bg-white aspect-[3/4] lg:aspect-auto lg:h-[40vh]"
                draggable={false}
              />
            </div>
            <p className="home-body-text text-[15px] leading-6 text-gray-700 font-bdogrotesk">
              {thumbnailProduct?.product || thumbnailProduct?.title}
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}